import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Calculator, ChevronRight, Users } from 'lucide-react';

// Déduit la surface d'un produit depuis son nom (ex: "Chapiteau 8x12m" -> 96 m²)
const parseSurface = (name) => {
  const m = /(\d+(?:[.,]\d+)?)\s*[x×]\s*(\d+(?:[.,]\d+)?)/i.exec(name || '');
  if (!m) return null;
  const w = parseFloat(m[1].replace(',', '.'));
  const l = parseFloat(m[2].replace(',', '.'));
  if (!w || !l) return null;
  return Math.round(w * l);
};

const isStructure = (p) =>
  p.category === 'barnums-tentes' && /^(chapiteau|tente|barnum)/i.test(p.name || '');

const EVENT_TYPES = [
  { id: 'assis', label: 'Repas assis', rate: 1.5, hint: 'tables rondes ou banquet' },
  { id: 'cocktail', label: 'Cocktail debout', rate: 0.95, hint: 'mange-debout et buffet' },
  { id: 'conference', label: 'Conférence', rate: 0.7, hint: 'chaises en rangées' },
];

const STRUCTURES = [
  { label: '4 × 8 m', surface: 32 },
  { label: '6 × 12 m', surface: 72 },
  { label: '8 × 12 m', surface: 96 },
  { label: '8 × 18 m', surface: 144 },
  { label: '10 × 15 m', surface: 150 },
  { label: '10 × 20 m', surface: 200 },
  { label: '10 × 25 m', surface: 250 },
  { label: '10 × 20 m + 6 × 12 m', surface: 272 },
  { label: '10 × 25 m + 8 × 12 m', surface: 346 },
  { label: '2 structures de 10 × 20 m', surface: 400 },
];

const Toggle = ({ active, onClick, label, testid }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testid}
    className={
      'px-4 py-2 rounded-full text-sm font-medium border transition-colors ' +
      (active
        ? 'bg-orange-500 border-orange-500 text-white'
        : 'bg-white border-gray-200 text-gray-600 hover:border-orange-300')
    }
  >
    {label}
  </button>
);

const SizeCalculator = () => {
  const [guests, setGuests] = useState(100);
  const [type, setType] = useState('assis');
  const [dance, setDance] = useState(true);
  const [buffet, setBuffet] = useState(true);
  const [kitchen, setKitchen] = useState(false);
  const [structures, setStructures] = useState([]);

  useEffect(() => {
    fetch(process.env.REACT_APP_BACKEND_URL + '/api/products')
      .then((r) => r.json())
      .then((data) => {
        const list = data
          .filter(isStructure)
          .map((p) => ({ ...p, surface: parseSurface(p.name) }))
          .filter((p) => p.surface)
          .sort((a, b) => a.surface - b.surface);
        setStructures(list);
      })
      .catch(() => setStructures([]));
  }, []);

  const count = Math.min(Math.max(parseInt(guests, 10) || 0, 0), 600);
  const rate = EVENT_TYPES.find((t) => t.id === type).rate;

  const baseArea = Math.round(count * rate);
  const danceArea = dance ? Math.round(count * 0.35) : 0;
  const buffetArea = buffet ? 22 : 0;
  const kitchenArea = kitchen ? 18 : 0;
  const total = baseArea + danceArea + buffetArea + kitchenArea;

  const match = STRUCTURES.find((s) => s.surface >= total) || STRUCTURES[STRUCTURES.length - 1];
  const enough = match.surface >= total;

  // Barnums du catalogue couvrant la surface estimée (seul ou en association)
  const singles = structures.filter((s) => s.surface >= total).slice(0, 3);
  const pairs = [];
  if (count > 0 && singles.length === 0) {
    for (let i = 0; i < structures.length; i += 1) {
      for (let j = i; j < structures.length; j += 1) {
        const sum = structures[i].surface + structures[j].surface;
        if (sum >= total) pairs.push({ a: structures[i], b: structures[j], surface: sum });
      }
    }
    pairs.sort((x, y) => x.surface - y.surface);
  }
  const suggestions =
    count === 0
      ? []
      : singles.length > 0
      ? singles.map((s) => ({
          key: s.id,
          label: s.name,
          surface: s.surface,
          price: s.price,
          image: s.image,
        }))
      : pairs.slice(0, 3).map((p) => ({
          key: p.a.id + '-' + p.b.id,
          label: p.a.id === p.b.id ? '2 × ' + p.a.name : p.a.name + ' + ' + p.b.name,
          surface: p.surface,
          price: 'Sur devis',
          image: p.b.image,
          combo: true,
        }));
  const biggest = structures.length ? structures[structures.length - 1] : null;

  const devisLink = (product) => {
    const params = new URLSearchParams({
      invites: String(count),
      surface: String(total),
      reception: EVENT_TYPES.find((t) => t.id === type).label,
      structure: product ? product.label : match.label,
    });
    return '/devis?' + params.toString();
  };

  return (
    <section
      className="bg-gray-900 text-white rounded-2xl p-7 sm:p-9 my-12"
      data-testid="size-calculator"
    >
      <div className="flex items-center gap-3 mb-2">
        <Calculator className="w-6 h-6 text-orange-400" />
        <h2 className="text-2xl font-bold text-white m-0">Calculez la taille de votre barnum</h2>
      </div>
      <p className="text-sm text-gray-400 mb-8">
        Indiquez votre nombre d'invités : nous estimons la surface nécessaire et la structure adaptée.
      </p>

      <div className="grid lg:grid-cols-2 gap-8">
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-3" htmlFor="guest-count">
            Nombre d'invités
          </label>
          <div className="flex items-center gap-4 mb-7">
            <div className="relative flex-1">
              <Users className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
              <input
                id="guest-count"
                type="number"
                min="1"
                max="600"
                value={guests}
                onChange={(e) => setGuests(e.target.value)}
                data-testid="calc-guests-input"
                className="w-full pl-11 pr-4 py-3 rounded-xl bg-gray-800 border border-gray-700 text-white text-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
              />
            </div>
          </div>
          <input
            type="range"
            min="10"
            max="400"
            step="5"
            value={count}
            onChange={(e) => setGuests(e.target.value)}
            data-testid="calc-guests-slider"
            className="w-full accent-orange-500 mb-8"
            aria-label="Nombre d'invités"
          />

          <p className="text-sm font-medium text-gray-300 mb-3">Type de réception</p>
          <div className="flex flex-wrap gap-2 mb-7">
            {EVENT_TYPES.map((t) => (
              <Toggle
                key={t.id}
                active={type === t.id}
                onClick={() => setType(t.id)}
                label={t.label}
                testid={'calc-type-' + t.id}
              />
            ))}
          </div>

          <p className="text-sm font-medium text-gray-300 mb-3">Espaces supplémentaires</p>
          <div className="flex flex-wrap gap-2">
            <Toggle active={dance} onClick={() => setDance(!dance)} label="Piste de danse" testid="calc-opt-dance" />
            <Toggle active={buffet} onClick={() => setBuffet(!buffet)} label="Buffet & bar" testid="calc-opt-buffet" />
            <Toggle active={kitchen} onClick={() => setKitchen(!kitchen)} label="Arrière-cuisine traiteur" testid="calc-opt-kitchen" />
          </div>
        </div>

        <div className="bg-gray-800 rounded-xl p-6 flex flex-col">
          <p className="text-xs uppercase tracking-wide text-gray-400 mb-1">Surface estimée</p>
          <p className="text-4xl font-bold text-orange-400 mb-6" data-testid="calc-total-area">
            {total} m²
          </p>

          <ul className="space-y-2 text-sm text-gray-300 mb-6">
            <li className="flex justify-between">
              <span>Espace {EVENT_TYPES.find((t) => t.id === type).label.toLowerCase()}</span>
              <span className="text-white font-medium">{baseArea} m²</span>
            </li>
            {dance && (
              <li className="flex justify-between">
                <span>Piste de danse</span>
                <span className="text-white font-medium">{danceArea} m²</span>
              </li>
            )}
            {buffet && (
              <li className="flex justify-between">
                <span>Buffet & bar</span>
                <span className="text-white font-medium">{buffetArea} m²</span>
              </li>
            )}
            {kitchen && (
              <li className="flex justify-between">
                <span>Arrière-cuisine</span>
                <span className="text-white font-medium">{kitchenArea} m²</span>
              </li>
            )}
          </ul>

          <div className="border-t border-gray-700 pt-5 mb-6">
            <p className="text-xs uppercase tracking-wide text-gray-400 mb-1">Structure conseillée</p>
            <p className="text-2xl font-semibold text-white" data-testid="calc-recommendation">
              {count === 0 ? '—' : match.label}
            </p>
            <p className="text-xs text-gray-400 mt-2">
              {count === 0
                ? "Saisissez votre nombre d'invités pour obtenir une recommandation."
                : enough
                ? 'Soit ' + match.surface + " m² couverts, avec la marge de circulation nécessaire."
                : 'Au-delà de 400 m², nous associons plusieurs structures : contactez-nous pour une étude sur mesure.'}
            </p>
          </div>

          <div className="mt-auto flex flex-col sm:flex-row gap-3">
            <Link
              to={devisLink(null)}
              className="inline-flex items-center justify-center gap-2 px-5 py-3 bg-orange-500 text-white text-sm font-semibold rounded-full hover:bg-orange-600 transition-colors"
              data-testid="calc-cta-devis"
            >
              Devis pour {count || 0} invités <ChevronRight className="w-4 h-4" />
            </Link>
            <Link
              to="/catalogue/barnums-tentes"
              className="inline-flex items-center justify-center gap-2 px-5 py-3 border border-gray-600 text-gray-200 text-sm font-medium rounded-full hover:border-orange-400 hover:text-orange-300 transition-colors"
              data-testid="calc-cta-catalogue"
            >
              Voir les barnums
            </Link>
          </div>
        </div>
      </div>

      {/* Barnums correspondants */}
      {count > 0 && (
        <div className="mt-10" data-testid="calc-suggestions">
          <h3 className="text-base md:text-lg font-semibold text-white mb-1">
            Nos barnums pour {count} invités
          </h3>
          <p className="text-sm text-gray-400 mb-6">
            Structures de notre parc couvrant au moins {total} m².
          </p>

          {suggestions.length > 0 ? (
            <div className="grid sm:grid-cols-3 gap-5">
              {suggestions.map((p) => (
                <div
                  key={p.key}
                  className="bg-gray-800 rounded-xl overflow-hidden flex flex-col"
                  data-testid={'calc-suggestion-' + p.key}
                >
                  <img src={p.image} alt={p.label} className="w-full aspect-[4/3] object-cover" loading="lazy" />
                  <div className="p-5 flex flex-col flex-1">
                    <p className="text-sm font-semibold text-white mb-1">{p.label}</p>
                    <p className="text-xs text-gray-400 mb-3">
                      {p.surface} m² couverts · {p.price}
                      {p.combo ? ' · 2 structures associées' : ''}
                    </p>
                    <Link
                      to={devisLink(p)}
                      className="mt-auto inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-orange-500 text-white text-sm font-semibold rounded-full hover:bg-orange-600 transition-colors"
                      data-testid={'calc-request-' + p.key}
                    >
                      Demander ce pack <ChevronRight className="w-4 h-4" />
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="bg-gray-800 rounded-xl p-6">
              <p className="text-sm text-gray-300 mb-4">
                {biggest
                  ? 'Votre événement dépasse ' +
                    (2 * biggest.surface) +
                    " m² : nous l'équipons avec plusieurs structures associées. Parlons-en pour une configuration sur mesure."
                  : 'Chargement de nos structures disponibles...'}
              </p>
              {biggest && (
                <Link
                  to={devisLink(null)}
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-orange-500 text-white text-sm font-semibold rounded-full hover:bg-orange-600 transition-colors"
                  data-testid="calc-request-custom"
                >
                  Demander une étude sur mesure <ChevronRight className="w-4 h-4" />
                </Link>
              )}
            </div>
          )}
        </div>
      )}

      <p className="text-xs text-gray-500 mt-6">
        Estimation indicative basée sur nos installations en Normandie. La configuration finale dépend du terrain,
        du mobilier choisi et des accès.
      </p>
    </section>
  );
};

export default SizeCalculator;
